import colors from "tailwindcss/colors";

import { fillColors } from "@/components/UsageCharts/constants";

export const REQUESTS_TAB_INDEX = 0;
export const ANALYTICS_TAB_INDEX = 1;

export const CHART_CARD_HEIGHT_CLASS = "h-[320px]";

export const ANALYTICS_GRAPH_FULLSCREEN_DIALOG_CLASS =
  "flex max-h-[80vh] w-[min(calc(100vw-2rem),96vw)] max-w-[1400px] flex-col gap-4 overflow-hidden p-6";

export const ANALYTICS_GRAPH_FULLSCREEN_CHART_HEIGHT_CLASS = "h-[min(400px,calc(55vh-6rem))]";

export const ANALYTICS_GRAPH_CHART_CARD_CLASS =
  "flex min-h-0 flex-col rounded-lg border border-gray-100 bg-white p-4";

export const ANALYTICS_CHART_TOOLBAR_BUTTON_CLASS =
  "h-7 w-7 shrink-0 text-gray-500 hover:text-gray-900";

export const ANALYTICS_CHART_LOADING_LABEL = "Loading chart…";

export const DEFAULT_CHART_DOWNLOAD_FILENAME = "chart";
export const CHART_DOWNLOAD_FILE_FORMAT = "image/png";

export const ANALYTICS_GRAPH_GRID_CONTAINER_MIN_WIDTH = "42rem";

export const FULLSCREEN_RESET_KEY_SUFFIX = ":fullscreen";

export const CHART_PADDING = {
  top: 8,
  right: 12,
  bottom: 8,
  left: 12,
};
export const CHART_LINE_STROKE_WIDTH = 2;
export const CHART_SERIES_STROKE_WIDTH_NONE = 0;
export const CHART_LINE_MARKER_SIZE = 4;
export const CHART_SMOOTH_TENSION = 0.45;
export const AG_CHARTS_BAR_CORNER_RADIUS = 4;
export const CHART_AREA_GRADIENT_START_ALPHA = "3D";
export const CHART_AREA_GRADIENT_MID_ALPHA = "1F";
export const CHART_AREA_GRADIENT_END_ALPHA = "00";
export const CHART_AREA_GRADIENT_START_STOP = 0;
export const CHART_AREA_GRADIENT_MID_STOP = 0.45;
export const CHART_AREA_GRADIENT_END_STOP = 1;
export const CHART_AXIS_TITLE_FONT_SIZE = 11;
export const MODEL_BAR_CORNER_RADIUS = 8;
export const RUN_QUERY_SPINNER_SIZE = 4;
export const COMPACT_NUMBER_THRESHOLD = 1000;
export const VERY_SMALL_NUMBER_THRESHOLD = 0.01;
export const SMALL_NUMBER_THRESHOLD = 1;
export const VERY_SMALL_NUMBER_MAX_FRACTION_DIGITS = 6;
export const SMALL_NUMBER_MAX_FRACTION_DIGITS = 4;
export const DEFAULT_NUMBER_MAX_FRACTION_DIGITS = 2;
export const AXIS_LABEL_COLOR = "#6b7280";
export const AXIS_LINE_COLOR = "#e5e7eb";
export const GRID_LINE_COLOR = "#f3f4f6";
export const HEATMAP_EMPTY_COLOR = "#f9fafb";
export const MODELS_CHART_LIMIT = 10;
export const METADATA_KEYS_SELECT_LIMIT = 10;
export const DAYS_PER_WEEK = 7;
export const HEATMAP_GRADIENT_THICKNESS = 12;
export const HEATMAP_GRADIENT_LENGTH = 180;
export const HEATMAP_CELL_STROKE_COLOR = "#ffffff";
export const HEATMAP_CELL_STROKE_WIDTH = 1;
export const CHART_SELECTION_DEBOUNCE_MS = 250;
export const CHART_SELECTION_IGNORE_MS = 750;
export const LATENCY_AVERAGE_LINE_DASH = [5, 5];

export const analyticsChartTitles = {
  latency: "Latency over time",
  requests: "Requests over time",
  tokens: "Tokens over time",
  cost: "Cost",
  requestActivity: "Requests per model",
  requestActivityPromptTemplate: "Requests per prompt",
  statusHealth: "Requests by status",
  outputShape: "Response format",
  cacheReasoning: "Cached & thinking tokens",
  topFieldKeys: "Top keys",
  errorTypes: "Error types",
  toolsUsage: "Tool usage",
} as const;

export const analyticsChartDescriptions = {
  requests: "Total request volume for each period with your current filters.",
  tokens: "Input and output tokens for each period.",
  cost: "Estimated spend per period for your current filters.",
  requestActivityTotal: "Top models by request count.",
  requestActivityPromptTemplateTotal: "Top prompt templates by request count.",
  requestActivityDaily: "Requests by time period and model.",
  requestActivityPromptTemplateDaily: "Requests by time period and prompt template.",
  latency:
    "Latency in seconds for each period. Use Breakdown to compare top models or prompt templates.",
  statusHealth: "Request volume each period, split by status.",
  outputShape: "Share of JSON, tool-call, and plain-text responses each period.",
  outputShapePie: "Share of JSON, tool-call, and plain-text responses over the selected range.",
  cacheReasoning: "Cached and thinking tokens each period.",
  providerCost: "Providers ranked by total cost in this range.",
  promptCost: "Prompts ranked by total cost in this range.",
  tagCost: "Tags ranked by total cost in this range.",
  metadataCost: "Costs for each value of the selected metadata key in this range.",
  metadataCostAllKeys: "Metadata key/value pairs ranked by total cost in this range.",
  errorTypes: "Most common error types in this range.",
  metadataKeys: "Metadata keys that appear most often on requests.",
  outputKeys: "Structured output keys that appear most often.",
  toolsUsage: "Top tools by how many requests used them.",
} as const;

export const analyticsChartInteractionHints = {
  requests:
    "Click a bar or point to filter to that time period. Drag on the chart to narrow the date range. Use Chart below to switch between bar and line.",
  tokens:
    "Drag on the chart to narrow the date range. Use Chart below to switch between bar and line.",
  cost: "Drag on the chart to narrow the date range. Use Chart below on the total view; use Breakdown for provider, prompt, tag, or metadata views.",
  requestActivityTotal: "Click a bar to filter by model.",
  requestActivityPromptTemplateTotal: "Click a bar to add a prompt template filter.",
  requestActivityDaily: "Click a cell to filter by model and that time bucket.",
  requestActivityPromptTemplateDaily:
    "Click a cell to filter by prompt template and that time bucket.",
  latency: "Drag on the chart to narrow the date range. Use Breakdown for model or prompt views.",
  statusHealth:
    "Click a bar segment to filter by status and that time bucket. Use Chart below to switch between bar and line.",
  outputShape:
    "Drag on the chart to narrow the date range. Use Chart below to switch between line and pie.",
  outputShapePie: "Hover a slice for share and request count.",
  cacheReasoning: "Drag on the chart to narrow the date range.",
  providerCost: "Click a bar to filter by provider.",
  promptCost: "Click a bar to add a prompt filter.",
  tagCost: "Click a bar to filter by tag.",
  metadataCost:
    "Click a bar to filter by metadata key and value. Use Key to focus on one metadata field.",
  errorTypes: "Click a bar to filter by error type.",
  metadataKeys: "Click a bar to open structured search for that metadata key.",
  outputKeys: "Click a bar to open structured search for that output key.",
  toolsUsage: "Click a bar to filter by tool name.",
} as const;

const billingChartPalette = {
  blueLight: fillColors[0],
  blueMid: fillColors[1],
  blue: fillColors[2],
  blueDark: fillColors[3],
  fallbackIndigo: fillColors[5],
  fallbackPurple: fillColors[9],
};

const primaryLineColor = billingChartPalette.blueDark;

const latencyChartPalette = {
  average: colors.sky[400],
  p50: colors.emerald[400],
  p90: colors.amber[400],
  p95: colors.violet[500],
};

export const analyticsChartColors = {
  requests: primaryLineColor,
  tokens: primaryLineColor,
  tokensInput: billingChartPalette.blueMid,
  tokensOutput: primaryLineColor,
  cost: primaryLineColor,
  models: primaryLineColor,
  latencyAverage: latencyChartPalette.average,
  latencyP50: latencyChartPalette.p50,
  latencyP90: latencyChartPalette.p90,
  latencyP95: latencyChartPalette.p95,
  statusSuccess: colors.emerald[400],
  statusWarning: latencyChartPalette.p90,
  statusError: colors.red[500],
  requestActivityHeatmap: [
    billingChartPalette.blueLight,
    billingChartPalette.blueMid,
    billingChartPalette.blue,
    primaryLineColor,
  ],
};

export const ANALYTICS_CUSTOM_SERIES_COLORS = [
  analyticsChartColors.tokensInput,
  analyticsChartColors.tokensOutput,
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
] as const;

const ANALYTICS_CUSTOM_PRIMARY_COLOR_INDEX = 2;
export const ANALYTICS_CUSTOM_PRIMARY_COLOR =
  ANALYTICS_CUSTOM_SERIES_COLORS[ANALYTICS_CUSTOM_PRIMARY_COLOR_INDEX];
